import { API_URL } from "../utils/urls"; // PT-REACT-URLS v1
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

// Backend: app/Controllers/DonationApi.php (CodeIgniter). Razorpay secret stays on the server.
const API_BASE = API_URL + "donation";
const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";
const ORG_NAME = "Dr. Pravin Bhai Togadia";
const FALLBACK_COLOR = "#f7941d";

const formatINR = (value) => "₹" + Number(value).toLocaleString("en-IN");

// Load Razorpay Checkout only once, and only when someone actually donates.
let razorpayLoader = null;
const loadRazorpay = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (razorpayLoader) return razorpayLoader;

  razorpayLoader = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT;
    script.async = true;
    script.onload = () => resolve(Boolean(window.Razorpay));
    script.onerror = () => {
      razorpayLoader = null;
      script.remove();
      resolve(false);
    };
    document.body.appendChild(script);
  });

  return razorpayLoader;
};

// Form-encoded POST keeps this a "simple" CORS request (no preflight needed).
const postForm = (url, data) =>
  axios
    .post(url, new URLSearchParams(data), { validateStatus: () => true })
    .then((response) => response.data || {});

// Use the same colour as the site's own orange .btn-primary buttons.
const toHex = (color) => {
  const m = String(color).match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  if (!m) return String(color).startsWith("#") ? color : FALLBACK_COLOR;
  return "#" + [m[1], m[2], m[3]].map((n) => Number(n).toString(16).padStart(2, "0")).join("");
};

const siteButtonColor = () => {
  try {
    const probe = document.createElement("button");
    probe.className = "btn btn-primary";
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    document.body.appendChild(probe);
    const color = getComputedStyle(probe).backgroundColor;
    probe.remove();
    if (color && color !== "transparent" && !/rgba\(0,\s*0,\s*0,\s*0\)/.test(color)) {
      return toHex(color);
    }
  } catch (e) {
    // fall through to the default colour
  }
  return FALLBACK_COLOR;
};

const EMPTY_FORM = { name: "", email: "", phone: "", utr: "" };

const DonateCampaignDetail = () => {
  const { id } = useParams();

  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [amount, setAmount] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | processing | success
  const [message, setMessage] = useState(null); // { type, text }
  const [receipt, setReceipt] = useState(null);

  const [accent, setAccent] = useState(FALLBACK_COLOR);
  const isMounted = useRef(true);

  useEffect(() => {
    setAccent(siteButtonColor());
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Load campaign details (title, image, description, amount options) from the admin panel.
  useEffect(() => {
    let active = true;

    setLoading(true);
    setLoadError("");
    setCampaign(null);
    setStatus("idle");
    setMessage(null);
    setReceipt(null);

    axios
      .get(`${API_BASE}/campaign/${encodeURIComponent(id)}`, { validateStatus: () => true })
      .then((response) => {
        if (!active) return;
        if (response.data?.success && response.data.data) {
          const data = response.data.data;
          setCampaign(data);
          const first = Array.isArray(data.amount_options) ? data.amount_options[0] : null;
          setAmount(String(first ?? data.min_amount ?? ""));
        } else {
          setLoadError(response.data?.message || "This campaign is not available.");
        }
      })
      .catch(() => {
        if (active) setLoadError("Could not load the campaign. Please check your connection and try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [id]);

  const minAmount = campaign?.min_amount || 100;
  const maxAmount = campaign?.max_amount || 100000;
  const amountOptions = Array.isArray(campaign?.amount_options) ? campaign.amount_options : [];
  const amountIsValid =
    /^[0-9]{1,6}$/.test(amount) && Number(amount) >= minAmount && Number(amount) <= maxAmount;
  const busy = status === "processing";

  // payment_mode comes from the server: "live", "test" or "off" (Razorpay keys not set up).
  // Test-mode checkout is only shown when the page URL has ?rzp_test=1, so public donors never see it.
  const paymentMode = campaign?.payment_mode || "off";
  const testCheckout =
    paymentMode === "test" && new URLSearchParams(window.location.search).has("rzp_test");
  const showRazorpay = paymentMode === "live" || testCheckout;
  const showQr = paymentMode !== "live" && Boolean(campaign?.qr_image);

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const validate = () => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();

    if (!amountIsValid) {
      return `Please enter a whole-number amount between ${formatINR(minAmount)} and ${formatINR(maxAmount)}.`;
    }
    if (!name || name.length > 150) return "Please enter your name.";
    if (!/^[0-9+\-\s()]{7,30}$/.test(phone)) return "Please enter a valid mobile number.";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address.";
    return "";
  };

  const verifyPayment = async (response, order) => {
    if (isMounted.current) {
      setMessage({ type: "info", text: "Confirming your payment, please wait..." });
    }

    try {
      const result = await postForm(`${API_BASE}/verify-payment`, {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      });

      if (!isMounted.current) return;

      if (result.success) {
        setReceipt({
          mode: "razorpay",
          name: form.name.trim(),
          paymentId: response.razorpay_payment_id,
          amount: order.amount / 100,
        });
        setStatus("success");
        setMessage(null);
      } else {
        setStatus("idle");
        setMessage({
          type: "warning",
          text:
            `${result.message || "We could not confirm your payment yet."} ` +
            `If money was deducted, please keep this payment ID for reference: ${response.razorpay_payment_id}`,
        });
      }
    } catch (error) {
      if (!isMounted.current) return;
      setStatus("idle");
      setMessage({
        type: "warning",
        text:
          "We could not confirm your payment because of a network problem. " +
          `If money was deducted, please keep this payment ID for reference: ${response.razorpay_payment_id}`,
      });
    }
  };

  const handleDonate = async (event) => {
    event.preventDefault();
    if (busy || !campaign || !showRazorpay) return;

    const problem = validate();
    if (problem) {
      setMessage({ type: "danger", text: problem });
      return;
    }

    setStatus("processing");
    setMessage(null);

    try {
      const loaded = await loadRazorpay();
      if (!loaded) {
        throw new Error("The payment gateway could not be loaded. Please check your internet connection and try again.");
      }

      const order = await postForm(`${API_BASE}/create-order`, {
        campaign_id: String(campaign.id),
        donor_name: form.name.trim(),
        donor_email: form.email.trim(),
        donor_phone: form.phone.trim(),
        amount: String(Number(amount)),
      });

      if (!order.success || !order.order_id || !order.key_id) {
        throw new Error(order.message || "Unable to start the payment. Please try again.");
      }

      const checkout = new window.Razorpay({
        key: order.key_id,
        order_id: order.order_id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: ORG_NAME,
        description: campaign.title,
        prefill: {
          name: form.name.trim(),
          email: form.email.trim(),
          contact: form.phone.trim(),
        },
        notes: { campaign_id: String(campaign.id) },
        theme: { color: accent },
        handler: (response) => verifyPayment(response, order),
        modal: {
          ondismiss: () => {
            if (!isMounted.current) return;
            setStatus("idle");
            setMessage({ type: "warning", text: "Payment was not completed. You can try again." });
          },
        },
      });

      checkout.on("payment.failed", (response) => {
        if (!isMounted.current) return;
        const reason = response?.error?.description;
        setMessage({
          type: "danger",
          text: reason ? `Payment failed: ${reason}` : "Payment failed. Please try again.",
        });
      });

      checkout.open();
    } catch (error) {
      if (!isMounted.current) return;
      setStatus("idle");
      setMessage({
        type: "danger",
        text: error?.message || "Unable to start the payment. Please try again.",
      });
    }
  };

  // UPI QR: the donor pays in their UPI app, then sends their details + UTR so the
  // donation is recorded as "pending" and the admin can verify it against the bank statement.
  const handleQrSubmit = async () => {
    if (busy || !campaign || !showQr) return;

    const problem = validate();
    if (problem) {
      setMessage({ type: "danger", text: problem });
      return;
    }

    const utr = form.utr.trim();
    if (!/^[A-Za-z0-9]{6,35}$/.test(utr)) {
      setMessage({
        type: "danger",
        text: "Please enter the UPI transaction ID / UTR shown in your UPI app after paying (letters and numbers only).",
      });
      return;
    }

    setStatus("processing");
    setMessage(null);

    try {
      const result = await postForm(`${API_BASE}/qr-submit`, {
        campaign_id: String(campaign.id),
        donor_name: form.name.trim(),
        donor_email: form.email.trim(),
        donor_phone: form.phone.trim(),
        amount: String(Number(amount)),
        upi_reference: utr,
      });

      if (!isMounted.current) return;

      if (result.success) {
        setReceipt({
          mode: "qr",
          name: form.name.trim(),
          paymentId: utr,
          amount: Number(amount),
        });
        setStatus("success");
      } else {
        setStatus("idle");
        setMessage({ type: "danger", text: result.message || "Could not submit your details. Please try again." });
      }
    } catch (error) {
      if (!isMounted.current) return;
      setStatus("idle");
      setMessage({ type: "danger", text: "Could not submit your details because of a network problem. Please try again." });
    }
  };

  const donateAgain = () => {
    setStatus("idle");
    setReceipt(null);
    setMessage(null);
    setForm(EMPTY_FORM);
    setAmount(String(amountOptions[0] ?? minAmount));
  };

  if (loading) {
    return (
      <div className="container text-center py-5">
        <div className="spinner-border" role="status" aria-label="Loading campaign"></div>
      </div>
    );
  }

  if (loadError || !campaign) {
    return (
      <div className="container text-center py-5">
        <p>{loadError || "This campaign is not available."}</p>
        <Link to="/donationform" className="btn btn-secondary mt-3">
          Back to campaigns
        </Link>
      </div>
    );
  }

  return (
    <div className="container my-5 campaign-donate" style={{ "--donate-accent": accent }}>
      <style>{`
        .campaign-donate .amount-chip {
          border: 1px solid var(--donate-accent, ${FALLBACK_COLOR});
          color: var(--donate-accent, ${FALLBACK_COLOR});
          background: transparent;
          border-radius: 8px;
          padding: 10px 18px;
          font-weight: 600;
          min-width: 96px;
        }
        .campaign-donate .amount-chip.active,
        .campaign-donate .amount-chip:hover:not(:disabled) {
          background: var(--donate-accent, ${FALLBACK_COLOR});
          color: #fff;
        }
        .campaign-donate .donate-submit {
          background: var(--donate-accent, ${FALLBACK_COLOR});
          border-color: var(--donate-accent, ${FALLBACK_COLOR});
          color: #fff;
        }
        .campaign-donate .donate-submit:disabled { opacity: 0.7; }
      `}</style>

      <div className="row g-4 align-items-start">
        <div className="col-lg-6">
          {campaign.image && (
            <img
              src={campaign.image}
              alt={campaign.title || "Donation campaign"}
              className="img-fluid rounded w-100"
              style={{ maxHeight: "450px", objectFit: "cover" }}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          )}
          <h1 className="h3 mt-4 mb-3">{campaign.title}</h1>
          {campaign.description && (
            <p style={{ whiteSpace: "pre-line" }}>{campaign.description}</p>
          )}
          <Link to="/donationform">Back to campaigns</Link>
        </div>

        <div className="col-lg-6">
          <div className="card shadow-sm">
            <div className="card-body p-4">
              {status === "success" && receipt ? (
                <div className="text-center py-3">
                  <h2 className="h4 mb-3">Thank you, {receipt.name}!</h2>
                  {receipt.mode === "qr" ? (
                    <>
                      <p className="mb-1">
                        We have received your details for a donation of{" "}
                        <strong>{formatINR(receipt.amount)}</strong> to <strong>{campaign.title}</strong>.
                      </p>
                      <p className="text-muted small mb-4">
                        UPI reference: {receipt.paymentId}. Your donation will be confirmed after we verify the payment.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="mb-1">
                        Your donation of <strong>{formatINR(receipt.amount)}</strong> for{" "}
                        <strong>{campaign.title}</strong> was received.
                      </p>
                      <p className="text-muted small mb-4">Payment ID: {receipt.paymentId}</p>
                    </>
                  )}
                  <button type="button" className="btn donate-submit me-2" onClick={donateAgain}>
                    Donate again
                  </button>
                  <Link to="/donationform" className="btn btn-outline-secondary">
                    Other campaigns
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleDonate} noValidate>
                  <h2 className="h5 mb-3">Choose donation amount</h2>

                  {amountOptions.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {amountOptions.map((value) => {
                        const selected = String(value) === amount;
                        return (
                          <button
                            type="button"
                            key={value}
                            className={`amount-chip${selected ? " active" : ""}`}
                            aria-pressed={selected}
                            disabled={busy}
                            onClick={() => setAmount(String(value))}
                          >
                            {formatINR(value)}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  <label htmlFor="donation-amount" className="form-label">
                    Or enter your own amount (₹)
                  </label>
                  <input
                    id="donation-amount"
                    type="text"
                    inputMode="numeric"
                    className="form-control"
                    value={amount}
                    disabled={busy}
                    onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    required
                  />
                  <div className="form-text mb-3">
                    Minimum {formatINR(minAmount)}, maximum {formatINR(maxAmount)}
                  </div>

                  {(showRazorpay || showQr) && (
                    <>
                      {testCheckout && (
                        <div className="alert alert-warning py-2 small">Razorpay TEST mode: no real money is charged.</div>
                      )}

                      <label htmlFor="donor-name" className="form-label">Full name *</label>
                      <input
                        id="donor-name"
                        type="text"
                        className="form-control mb-3"
                        value={form.name}
                        maxLength={150}
                        autoComplete="name"
                        disabled={busy}
                        onChange={updateField("name")}
                        required
                      />

                      <label htmlFor="donor-phone" className="form-label">Mobile number *</label>
                      <input
                        id="donor-phone"
                        type="tel"
                        className="form-control mb-3"
                        value={form.phone}
                        maxLength={30}
                        autoComplete="tel"
                        disabled={busy}
                        onChange={updateField("phone")}
                        required
                      />

                      <label htmlFor="donor-email" className="form-label">Email (optional)</label>
                      <input
                        id="donor-email"
                        type="email"
                        className="form-control"
                        value={form.email}
                        maxLength={190}
                        autoComplete="email"
                        disabled={busy}
                        onChange={updateField("email")}
                      />
                    </>
                  )}

                  {showRazorpay && (
                    <>
                      <button type="submit" className="btn btn-lg w-100 mt-3 donate-submit" disabled={busy}>
                        {busy ? "Processing..." : `Donate${amountIsValid ? " " + formatINR(amount) : ""}`}
                      </button>
                      <p className="text-muted small text-center mt-2 mb-0">
                        Secure payment by Razorpay: UPI, cards, net banking and wallets
                      </p>
                    </>
                  )}

                  {showQr && (
                    <div className={`text-center ${showRazorpay ? "border-top pt-3 mt-3" : "mt-4"}`}>
                      <h3 className="h6 mb-2">
                        {showRazorpay ? "Or scan and pay by UPI" : "Step 1: Scan and pay by UPI"}
                      </h3>
                      <img
                        src={campaign.qr_image}
                        alt={`UPI QR code for ${campaign.title}`}
                        className="img-fluid border rounded p-2 bg-white"
                        style={{ maxWidth: "240px" }}
                      />
                      <p className="mt-2">
                        {amountIsValid
                          ? <>Scan with any UPI app (GPay, PhonePe, Paytm) and pay <strong>{formatINR(amount)}</strong>.</>
                          : "Scan with any UPI app (GPay, PhonePe, Paytm) and enter your amount."}
                      </p>

                      <div className="text-start">
                        <label htmlFor="donor-utr" className="form-label">
                          Step 2: UPI transaction ID / UTR *
                        </label>
                        <input
                          id="donor-utr"
                          type="text"
                          className="form-control"
                          value={form.utr}
                          maxLength={35}
                          autoComplete="off"
                          placeholder="e.g. 412345678901"
                          disabled={busy}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              utr: event.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 35),
                            }))
                          }
                        />
                        <div className="form-text">
                          After paying, copy the 12-digit UTR / transaction ID from your UPI app's payment details.
                        </div>
                      </div>

                      <button
                        type="button"
                        className="btn btn-lg w-100 mt-3 donate-submit"
                        disabled={busy}
                        onClick={handleQrSubmit}
                      >
                        {busy ? "Submitting..." : "I have paid, submit details"}
                      </button>
                    </div>
                  )}

                  {message && (
                    <div className={`alert alert-${message.type} mt-3 mb-0`} role="alert">
                      {message.text}
                    </div>
                  )}

                  {!showRazorpay && !showQr && (
                    <div className="alert alert-info mb-0">
                      Online donation for this campaign will be available soon.
                    </div>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonateCampaignDetail;
